import React from 'react';
import {Trans, useTranslation} from 'react-i18next';

import {Modal, ModalActions, ModalHeader, ModalSubmitButton, showModal} from '@edna/components';
import {Divider, SkeletonItem, Stack} from '@edna/components/primitives';

import {InlineLink, InlineLinkButton, Text} from 'src/components/primitives';
import {EChannelType} from 'src/constants';
import {EViberTrafficType, UNDEFINED_COUNTRY} from 'src/containers/TariffsCatalog';
import {getCountryLabel} from 'src/utils';

import {TAvailableTariff} from '../../definitions';
import * as S from './style';
import {useCountriesPrices, useLicensePrices} from './usePrices';
import {usePricingLink} from './usePricingLink';

type TModalProps = {
  tariff: TAvailableTariff;
  closeModal: TEmptyFunction;
};

const TRAFFIC_TYPES = Object.values(EViberTrafficType);

const ViberPricingModal = React.memo<TModalProps>(({closeModal, tariff}) => {
  const {t} = useTranslation();

  const countriesPrices = useCountriesPrices(tariff);

  return (
    <Modal width="830px">
      <ModalHeader>{t('Tariffs:viberPricing.modalTitle')}</ModalHeader>
      <Stack>
        <S.CollapsibleSection
          isOpenDefault
          label={t('Tariffs:viberPricing.pricing', {currency: countriesPrices?.currency})}
          channelType={EChannelType.VIBER}
        >
          <S.Tr noBorder>
            <S.Th>{t('Tariffs:viberPricing.country')}</S.Th>
            {TRAFFIC_TYPES.map((trafficType) => (
              <S.Th key={trafficType}>{t(`Tariffs:viberPricing.traffic.${trafficType}`)}</S.Th>
            ))}
          </S.Tr>
          <S.ScrollContainer size="0">
            {countriesPrices?.prices.map((item, index) => (
              <S.Tr key={index}>
                <S.Td>
                  {item.country === UNDEFINED_COUNTRY
                    ? t('Country:other')
                    : getCountryLabel(t, item.country)}
                </S.Td>
                {TRAFFIC_TYPES.map((trafficType) => (
                  <S.Td key={trafficType}>{item.prices[trafficType]}</S.Td>
                ))}
              </S.Tr>
            ))}
          </S.ScrollContainer>
        </S.CollapsibleSection>
        <Divider />
        <Text secondary size="XS" topIndent="2">
          {t('Tariffs:pricing.vatHint')}
        </Text>
      </Stack>
      <ModalActions>
        <ModalSubmitButton onClick={closeModal} appearance="secondary">
          {t('Common:button.close')}
        </ModalSubmitButton>
      </ModalActions>
    </Modal>
  );
});

ViberPricingModal.displayName = 'ViberPricingModal';

const showViberPricingModal = (tariff: TAvailableTariff) =>
  showModal((closeModal) => <ViberPricingModal closeModal={closeModal} tariff={tariff} />);

type TViberPricingProps = {
  subjectId?: number;
  tariff: TAvailableTariff;
};

const ViberPricing = React.memo<TViberPricingProps>(({subjectId, tariff}) => {
  const {t} = useTranslation();

  const licensePrices = useLicensePrices(tariff);
  const pricingLink = usePricingLink({channelType: EChannelType.VIBER, subjectId});

  if (!licensePrices || !tariff) {
    return (
      <S.LoadingContainer>
        <SkeletonItem height="50px" />
        <SkeletonItem height="50px" />
      </S.LoadingContainer>
    );
  }

  return (
    <S.Pricing>
      <Text weight={500}>
        {t('Tariffs:viberPricing.licensePrice')}
        <Text secondary size="S" topIndent="1">
          {t('Tariffs:viberPricing.licensePriceHint', {
            currency: licensePrices.currency,
            licensePrice: licensePrices.licensePrice,
          })}
        </Text>
      </Text>
      <Text weight={500}>
        {t('Tariffs:viberPricing.detailPricing')}
        <Text secondary size="S" topIndent="1">
          {t('Tariffs:viberPricing.packagePrice', {
            currency: licensePrices.currency,
            packagePrice: licensePrices.packagePrice,
          })}
        </Text>
        <Text secondary size="S" topIndent="1">
          <Trans i18nKey="Tariffs:viberPricing.detailPricingHint">
            {subjectId ? (
              <InlineLinkButton onClick={() => showViberPricingModal(tariff)} />
            ) : (
              <InlineLink href={pricingLink} />
            )}
          </Trans>
        </Text>
      </Text>
      <Text secondary size="XS">
        <Trans i18nKey="Tariffs:viberPricing.pricingHint" />
      </Text>
    </S.Pricing>
  );
});

ViberPricingModal.displayName = 'ViberPricingModal';

export {ViberPricing};
